/**
 * Pantalla de inicio tras login o redirección RBAC.
 * El mapa es el módulo común a todos los roles: se prioriza /map cuando hay acceso a `mapa`.
 */
export function firstAllowedPath(
  allowedModules: string[] | undefined,
  isAdministrador: boolean,
  allowedActions?: string[] | undefined
): string {
  if (isAdministrador) return '/map';
  if (allowedModules === undefined) return '/map';
  /** Sin módulos en BD / error al cargar: el mapa es el módulo común; no mandar a perfil. */
  if (allowedModules.length === 0) return '/map';

  const actions = allowedActions;
  const strict = Array.isArray(actions);
  const hasGrant = (g: string) => (actions ?? []).includes(g);
  const hasAnyInModule = (moduleKey: string) =>
    (actions ?? []).some((a) => a === moduleKey || a.startsWith(`${moduleKey}:`));

  const canReach = (moduleKey: string, grant: string | null) => {
    if (!allowedModules.includes(moduleKey)) return false;
    if (!grant) return true;
    if (!strict) return true;
    return hasGrant(grant);
  };

  const order: Array<{ path: string; moduleKey: string; grant: string | null }> = [
    { path: '/map', moduleKey: 'mapa', grant: null },
    { path: '/dashboard', moduleKey: 'dashboards', grant: null },
    { path: '/streams', moduleKey: 'xtam_vms', grant: 'xtam_vms:streaming' },
    { path: '/recordings', moduleKey: 'xtam_vms', grant: 'xtam_vms:recordings_preview' },
    { path: '/analytics', moduleKey: 'xtam_vms', grant: 'xtam_vms:analytics' },
    { path: '/download-certificates', moduleKey: 'xtam_vms', grant: 'xtam_vms:recordings_download' },
    { path: '/admin/button-alerts', moduleKey: 'alarmas', grant: 'alarmas:alerts_buttons_eu' },
    { path: '/admin/sites', moduleKey: 'administracion', grant: 'administracion:sites' },
    { path: '/admin/historic', moduleKey: 'administracion', grant: 'administracion:history' },
    { path: '/admin/miniiot', moduleKey: 'administracion', grant: 'administracion:miniiot' },
    { path: '/cameras', moduleKey: 'administracion', grant: 'administracion:cameras' },
    { path: '/admin/inventory', moduleKey: 'administracion', grant: 'administracion:inventory' },
    { path: '/security/roles', moduleKey: 'seguridad', grant: 'seguridad:roles' },
    { path: '/security/users', moduleKey: 'seguridad', grant: 'seguridad:users' },
    { path: '/security/audit', moduleKey: 'seguridad', grant: 'seguridad:users' }
  ];

  for (const { path, moduleKey, grant } of order) {
    if (canReach(moduleKey, grant)) return path;
  }

  /** Tiene XTAM en módulos pero solo submódulos no contemplados en la lista: cualquier acción XTAM. */
  if (strict && allowedModules.includes('xtam_vms') && hasAnyInModule('xtam_vms')) {
    return '/streams';
  }
  if (strict && allowedModules.includes('administracion') && hasAnyInModule('administracion')) {
    return '/admin/sites';
  }
  if (strict && allowedModules.includes('alarmas') && hasAnyInModule('alarmas')) {
    return '/admin/button-alerts';
  }
  if (strict && allowedModules.includes('seguridad') && hasAnyInModule('seguridad')) {
    if (hasGrant('seguridad:roles')) return '/security/roles';
    return '/security/users';
  }

  return '/map';
}
